/**
 * Project Workspace Page
 * Main working area for a single project with tabs
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  AlertTriangle
} from 'lucide-react';
import { addToRecentProjects } from '../db';
import { projectsApi } from '../lib/api';
import { WorkspaceHeader } from '../components/workspace/WorkspaceHeader';
import { WorkspaceTabs } from '../components/workspace/WorkspaceTabs';
import { MapTab } from '../components/workspace/tabs/MapTab';
import { PhotosTab } from '../components/workspace/tabs/PhotosTab';
import { AnomaliesTab } from '../components/workspace/tabs/AnomaliesTab';
import { ReportTab } from '../components/workspace/tabs/ReportTab';

interface Project {
  id: string;
  code: string;
  name: string;
  line?: string;
  status: string;
  progress: number;
  photoCount: number;
  anomalyCount: number;
  elementCount: number;
}

export function ProjectWorkspace() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      loadProject(projectId);
    }
  }, [projectId]);

  const loadProject = async (id: string) => {
    try {
      const response = await projectsApi.get(id);
      const proj = response.data.data;
      if (proj) {
        setProject(proj);
        // Add to recent projects
        await addToRecentProjects(id);
      } else {
        setError('Progetto non trovato');
      }
    } catch (err) {
      console.error('Failed to load project:', err);
      setError('Errore nel caricamento del progetto');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectUpdate = async () => {
    if (projectId) {
      await loadProject(projectId);
    }
  };

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/photos')) return 'photos';
    if (path.includes('/anomalies')) return 'anomalies';
    if (path.includes('/report')) return 'report';
    return 'map';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <AlertTriangle className="w-16 h-16 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {error || 'Project not found'}
        </h2>
        <p className="text-gray-500 mb-6">
          The requested project does not exist or is not accessible.
        </p>
        <button
          onClick={() => navigate('/projects')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Workspace Header */}
      <WorkspaceHeader 
        project={project as any} 
        onProjectUpdate={handleProjectUpdate}
      />

      {/* Tabs Navigation */}
      <WorkspaceTabs 
        projectId={project.id} 
        activeTab={getActiveTab()}
        counts={{
          elements: project.elementCount,
          photos: project.photoCount,
          anomalies: project.anomalyCount,
        }}
      />

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <Routes>
          <Route index element={<Navigate to="map" replace />} />
          <Route path="map" element={<MapTab project={project as any} />} />
          <Route path="photos" element={<PhotosTab projectId={project.id} />} />
          <Route path="anomalies" element={<AnomaliesTab project={project as any} />} />
          <Route path="report" element={<ReportTab project={project as any} />} />
        </Routes>
      </div>
    </div>
  );
}
